import { z } from 'zod';

/**
 * Zod schemas for static page JSON. Used in unit tests to validate bundled copy.
 * UI types live in {@link ../types.ts} — keep schema aligned when JSON shape changes.
 */

const staticListStyleSchema = z.enum(['decimal', 'lower-alpha', 'lower-roman']);

const staticTableColumnAlignmentSchema = z.enum(['left', 'center', 'right']);

const staticActionLinkSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
});

const staticActionBlockSchema = staticActionLinkSchema.extend({
  type: z.literal('action'),
});

const staticAppDownloadLinksBlockSchema = z.object({
  type: z.literal('appDownloadLinks'),
});

const staticImageBlockSchema = z.object({
  type: z.literal('image'),
  src: z.string().min(1),
  alt: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  loading: z.enum(['lazy', 'eager']).optional(),
  href: z.string().min(1).optional(),
});

const staticVideoBlockSchema = z.object({
  type: z.literal('video'),
  src: z.string().min(1),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  poster: z.string().min(1).optional(),
  ariaLabel: z.string().min(1).optional(),
  captions: z
    .object({
      src: z.string().min(1),
      srcLang: z.string().min(1),
      label: z.string().min(1),
    })
    .optional(),
});

const staticTableColumnSchema = z.object({
  key: z.string().optional(),
  header: z.string(),
  width: z.string().optional(),
  align: staticTableColumnAlignmentSchema.optional(),
  mobileAlign: staticTableColumnAlignmentSchema.optional(),
});

const staticTablePickerGroupSchema = z.object({
  option: z.string(),
  rows: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    })
  ),
});

const staticTablePickerBlockSchema = z.object({
  type: z.literal('table'),
  variant: z.literal('picker'),
  groups: z.array(staticTablePickerGroupSchema).min(1),
  selectLabel: z.string().optional(),
  rowHeader: z.string().optional(),
  valueHeader: z.string().optional(),
  caption: z.string().optional(),
  ariaLabel: z.string().optional(),
});

const staticTableGridBlockSchema = z.object({
  type: z.literal('table'),
  variant: z.literal('grid').optional(),
  columns: z.array(staticTableColumnSchema).min(1),
  rows: z.array(z.record(z.string(), z.string())),
  caption: z.string().optional(),
  ariaLabel: z.string().optional(),
});

const staticTableBlockSchema = z.union([staticTablePickerBlockSchema, staticTableGridBlockSchema]);

const staticLookupBlockSchema = z.object({
  type: z.literal('lookup'),
  source: z.string().min(1),
  title: z.string(),
  inputLabel: z.string(),
  placeholder: z.string(),
  clearLabel: z.string(),
  resultLabel: z.string(),
  validationMessage: z.string(),
  unavailableMessage: z.string(),
  unavailableHref: z.string().optional(),
  unavailableLinkLabel: z.string().optional(),
});

const staticContentSchema: z.ZodType<unknown> = (() => {
  const listBlockSchema: z.ZodType<unknown> = z.lazy(() =>
    z.object({
      type: z.literal('list'),
      intro: staticContentSchema.optional(),
      style: staticListStyleSchema.optional(),
      items: z.array(staticContentSchema).min(1),
      outro: staticContentSchema.optional(),
    })
  );

  return z.lazy(() =>
    z.union([
      z.string(),
      staticActionBlockSchema,
      staticAppDownloadLinksBlockSchema,
      staticImageBlockSchema,
      listBlockSchema,
      staticLookupBlockSchema,
      staticTableBlockSchema,
      staticVideoBlockSchema,
    ])
  );
})();

const staticPageSectionSchema = z
  .object({
    title: z.string().min(1),
    content: z.array(staticContentSchema),
    action: staticActionLinkSchema.optional(),
    contentNumbered: z.boolean().optional(),
    numbered: z.boolean().optional(),
  })
  .refine(section => section.content.length > 0 || section.action, {
    message: 'section needs content or an action',
    path: ['content'],
  });

const staticPageJsonDataSchema = z.object({
  title: z.string().min(1),
  lastUpdated: z.string().min(1).optional(),
  navigation: z.object({
    title: z.string(),
    intro: z.string(),
    backToTop: z.string(),
  }),
  sections: z.array(staticPageSectionSchema).min(1),
});

function refineStaticLookupDataset(dataset: { min: number; max: number; values: Record<string, string> }, ctx: z.RefinementCtx) {
  if (dataset.min > dataset.max) {
    ctx.addIssue({
      code: 'custom',
      message: 'min must be less than or equal to max',
      path: ['min'],
    });
    return;
  }

  for (const key of Object.keys(dataset.values)) {
    if (!/^\d+$/.test(key)) {
      ctx.addIssue({
        code: 'custom',
        message: `values key "${key}" must be a whole number`,
        path: ['values', key],
      });
      continue;
    }

    const numericKey = Number(key);

    if (numericKey < dataset.min || numericKey > dataset.max) {
      ctx.addIssue({
        code: 'custom',
        message: `values key "${key}" must be between ${dataset.min} and ${dataset.max}`,
        path: ['values', key],
      });
    }
  }

  for (let sum = dataset.min; sum <= dataset.max; sum += 1) {
    if (dataset.values[String(sum)] === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: `missing lookup value for sum ${sum} (expected every integer from min to max)`,
        path: ['values'],
      });
    }
  }
}

const staticLookupDatasetSchema = z
  .object({
    min: z.number().int(),
    max: z.number().int(),
    values: z.record(z.string(), z.string()),
  })
  .superRefine(refineStaticLookupDataset);

const staticLookupRegistrySchema = z.record(z.string(), staticLookupDatasetSchema);

function parseStaticPageJsonData(input: unknown) {
  return staticPageJsonDataSchema.parse(input);
}

function parseStaticLookupRegistry(input: unknown) {
  return staticLookupRegistrySchema.parse(input);
}

export { parseStaticLookupRegistry, parseStaticPageJsonData, staticContentSchema, staticLookupDatasetSchema, staticLookupRegistrySchema, staticPageJsonDataSchema };
